import { useLocation } from 'preact-iso'
import type { CompanySignalDto } from '@/api/leads'
import { CompanyAvatar } from '@/components/common/Avatar'
import { routes } from '@/utils/routes'
import { toSafeUrl } from './companyLinks'
import { LinkedInGlyph, GlobeGlyph } from './companyGlyphs'

interface CompanyNameCellProps {
  company: CompanySignalDto
  clientCompanyId?: string | null
  workspaceId?: string | null
}

/**
 * The company cell shared by the workspace, campaign and web Companies grids (ORB-623).
 *
 * Extracted because it was three identical copies and is now growing behaviour — the drift this
 * codebase has already been bitten by. Two things it has to get right:
 *
 * The name links INWARD to the company profile whenever there is a company to open.
 * `CompanySignalDto.CompanyId` is a grouping key that means different things at different scopes,
 * so `CompanyProfileId` is the only value safe to navigate on. That includes companies we have
 * identified but hold no record for — the profile renders those from the workspace's own rows and
 * says the detail is limited. Only a group nobody was matched to at all is left as plain text.
 *
 * The outward LinkedIn and website links STAY, as icons. They are what these grids offered before
 * this change, and quietly dropping an affordance during a migration is exactly how the last two
 * of these went wrong.
 */
export function CompanyNameCell({ company, clientCompanyId, workspaceId }: CompanyNameCellProps) {
  const location = useLocation()

  // Two different reasons the name may not be a link, and they owe the reader different answers.
  // Unmatched is about their data and worth explaining. Missing routing context is a caller that
  // did not pass its scope — nothing the reader can act on, so it gets no tooltip rather than an
  // explanation that would be untrue.
  const hasScope = Boolean(clientCompanyId && workspaceId)
  const profileHref =
    company.CompanyProfileId && clientCompanyId && workspaceId
      ? routes.scoped(clientCompanyId, workspaceId).companyProfile(company.CompanyProfileId)
      : undefined
  const unmatchedReason =
    hasScope && !company.CompanyProfileId
      ? "These people aren't matched to a company — usually a personal email address rather than a company one — so there's no company page to open."
      : undefined

  return (
    <div class="flex items-center gap-3">
      <CompanyAvatar companyName={company.CompanyName} logoUrl={company.CompanyLogoUrl} />
      <div class="flex items-center gap-1.5 min-w-0">
        {profileHref ? (
          <a
            href={profileHref}
            onClick={(e: MouseEvent) => {
              // Let modifier-clicks and middle-clicks do what the browser would; the href is real.
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
              e.preventDefault()
              location.route(profileHref)
            }}
            class="font-medium text-gray-900 dark:text-gray-100 hover:underline truncate"
            title={`Open ${company.CompanyName} profile`}
          >
            {company.CompanyName}
          </a>
        ) : (
          <span class="font-medium text-gray-900 dark:text-gray-100 truncate" title={unmatchedReason}>
            {company.CompanyName}
          </span>
        )}

        {company.CompanyLinkedInUrl && (
          <ExternalIcon href={company.CompanyLinkedInUrl} label={`${company.CompanyName} on LinkedIn`} kind="linkedin" />
        )}
        {company.CompanyWebsite && (
          <ExternalIcon href={company.CompanyWebsite} label={`${company.CompanyName} website`} kind="globe" />
        )}
      </div>
    </div>
  )
}

/** An outward icon link inside a grid row: the lead cell uses it for a person's LinkedIn profile too. */
export function ExternalIcon({ href, label, kind }: { href: string; label: string; kind: 'linkedin' | 'globe' }) {
  const safe = toSafeUrl(href)
  if (!safe) return null

  return (
    <a
      href={safe}
      target="_blank"
      rel="noopener noreferrer"
      title={label}
      aria-label={label}
      onClick={(e: MouseEvent) => e.stopPropagation()}
      class="flex-none text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
    >
      {kind === 'linkedin' ? <LinkedInGlyph /> : <GlobeGlyph />}
    </a>
  )
}
